import React, { useState, useEffect, useMemo } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { TbEdit, TbEye } from "react-icons/tb";
import DeleteTask from "../Assets/DeleteModal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const TaskDetails = () => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [taskDetailsCollection, setTaskDetailsCollection] = useState([]);
  const [search, setSearch] = useState("");
  const [AssignByFilter, setAssignByFilter] = useState("");
  const [AssignToFilter, setAssignToFilter] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const itemsPerPage = 30;
  const navigate = useNavigate();
  const Auth = localStorage.getItem("authToken");

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await fetch("https://backend.sanchalansetu.com/api/v1/get-task-list", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Auth}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch tasks");

      const result = await response.json();
      setTasks(result || []);
      setUsers(result.team_members || []);
      setTaskDetailsCollection(result || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const filteredTasks = useMemo(() => {
    const today = new Date();

    return taskDetailsCollection.filter((t) => {
      const titleMatch = t.title?.toLowerCase().includes(search.toLowerCase());
      const fromUserMatch = t.from_user?.toLowerCase().includes(search.toLowerCase());
      const toUserMatch = t.to_user?.toLowerCase().includes(search.toLowerCase());

      
      const createdDate = new Date(t.created_at);
      const isToday =
        createdDate.getDate() === today.getDate() &&
        createdDate.getMonth() === today.getMonth() &&
        createdDate.getFullYear() === today.getFullYear();

      let matchesAssignBy = true;
      if (AssignByFilter) {
        matchesAssignBy = t.from_user?.toLowerCase() === AssignByFilter.toLowerCase();
      }

      let matchesAssignTo = true;
      if (AssignToFilter) {
        matchesAssignTo = t.to_user?.toLowerCase() === AssignToFilter.toLowerCase();
      }

      return (
        (fromUserMatch || toUserMatch || titleMatch) &&
        matchesAssignBy &&
        matchesAssignTo &&
        isToday
      );
    });
  }, [taskDetailsCollection, search, AssignByFilter, AssignToFilter]);

  const totalPages = Math.ceil(filteredTasks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentTasks = filteredTasks.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const handleDeleteClick = (id) => {
    setItemToDelete(id);
    setDeleteModalShow(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`https://backend.sanchalansetu.com/api/v1/task/delete/group/${itemToDelete}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Auth}`,
        },
      });

      const errorText = await response.json();
      if (!response.ok) {
        toast.error(errorText.message || "Failed to delete task");
        return;
      }

      toast.success(errorText.message || "Task deleted successfully");
    } catch (error) {
      console.error("Error deleting task:", error);
    }
    fetchTasks();
    setDeleteModalShow(false);
    setItemToDelete(null);
  };

  const cancelDelete = () => {
    setDeleteModalShow(false);
    setItemToDelete(null);
  };

  const handleEditClick = (id) => {
    navigate(`/task-edit/${id}`);
  };

  const handleViewClick = (id) => {
    navigate(`/task-details/${id}`);
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="Task-Details-Container">
      <h2 className="text-xl font-semibold mb-4">Today's Task List</h2>

      <div className="flex justify-end flex-wrap gap-4 mb-4">
        <input
          type="text"
          placeholder="Search by task title..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="px-4 py-2 border rounded"
        />

        <select
          value={AssignByFilter}
          onChange={(e) => {
            setAssignByFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="px-2 py-1 border rounded"
        >
          <option value="">Assign By</option>
          {users.map((user) => (
            <option key={user.id} value={user.name}>
              {user.name}
            </option>
          ))}
        </select>

        <select
          value={AssignToFilter}
          onChange={(e) => {
            setAssignToFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="px-2 py-1 border rounded"
        >
          <option value="">Assign To</option>
          {users.map((user) => (
            <option key={user.id} value={user.name}>
              {user.name}
            </option>
          ))}
        </select>
      </div>

      <table className="w-full text-sm border task-table">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-4 py-2 text-left">#</th>
            <th className="px-4 py-2 text-left">Task Title</th>
            <th className="px-4 py-2 text-left">Assigned By</th>
            <th className="px-4 py-2 text-left">Assigned To</th>
            <th className="px-4 py-2 text-left">Created At</th>
            <th className="px-4 py-2 text-left">Action</th>
          </tr>
        </thead>
        <tbody>
          {currentTasks.length > 0 ? (
            currentTasks.map((t, index) => (
              <tr key={t.id || index} className="border-t">
                <td className="px-4 py-2">{startIndex + index + 1}</td>
                <td className="px-4 py-2">{t.title || "N/A"}</td>
                <td className="px-4 py-2">{t.from_user_name || "N/A"}</td>
                <td className="px-4 py-2">
                  <div className="flex items-start -space-x-4">
                    {t.team_members.map((member, i) => (
                      <div key={member.user_id} className="relative group">
                        <img
                          src={member.avatar}
                          alt={member.user_name}
                          className="w-10 h-10 rounded-full border-2 border-white shadow-sm cursor-pointer transition-all duration-200 group-hover:z-50 group-hover:-translate-y-1"
                          title={member.user_name}
                        />
                      </div>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-2">{formatDateTime(t.created_at)}</td>
                <td className="px-4 py-2 flex gap-2">
                  <button className="text-blue-500" onClick={() => handleViewClick(t.task_group_id)}>
                    <TbEye size={22} />
                  </button>
                  <button className="text-blue-500" onClick={() => handleEditClick(t.task_group_id)}>
                    <TbEdit size={22} />
                  </button>
                  <button className="text-red-500" onClick={() => handleDeleteClick(t.task_group_id)}>
                    <MdDeleteOutline size={22} />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" className="text-center py-4 text-gray-500">
                No tasks created today
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {deleteModalShow && (
        <DeleteTask
          itemName={currentTasks.find((p) => p.task_group_id === itemToDelete)?.title || "Unknown Task"}
          onClose={cancelDelete}
          onDelete={confirmDelete}
        />
      )}
    </div>
  );
};

export default TaskDetails;

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return isNaN(date.getTime()) ? "" : date.toLocaleString();
};
